import { Box, Button, Text } from '@user-portal/wikr-components';
import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { sendFeedback } from 'store/subscriptions/actions';

import { Cross, MoveBack } from 'components/Icon';
import Modal from 'components/Modal';
import OutlinedButton from 'components/OutlinedButton';

import { IFeedbackFormModal } from './types';
import validationSchema from './validation-scheme';

import * as S from './styled';

type FeedbackFormValues = { comment?: string; reason: string };

const FeedbackFormModal = ({ onMoveBack, onContinue, onCancel, onClose, trackLoad }: IFeedbackFormModal) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const submitSurvey = async (data: FeedbackFormValues) => {
        dispatch(sendFeedback(data));
        onContinue(data);
    };

    const { values, handleChange, handleSubmit, errors, handleBlur, touched, isValid } = useFormik<FeedbackFormValues>({
        validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: submitSurvey,
        initialValues: {
            comment: '',
            // Currently this form works only for the "other" reason of survey submission
            reason: 'other',
        },
    });

    const handleCancel = () => {
        dispatch(sendFeedback(values));
        onCancel(values);
    };

    const handleClose = () => {
        onClose();
    };

    const isError = Boolean(errors.comment && touched.comment);

    return (
        <Modal
            isOpen
            paddingX={0}
            paddingY={0}
            onClose={handleClose}
            transparentBackground
            transparentOverlay={false}
            trackLoad={trackLoad}
        >
            <S.ModalContent>
                <S.ModalHeaderTitle>
                    {onMoveBack && (
                        <S.IconWrapper onClick={onMoveBack}>
                            <MoveBack />
                        </S.IconWrapper>
                    )}
                    <S.TitleText>
                        <Text type="large-text" medium>
                            {t('subscription.text.cancel')}
                        </Text>
                    </S.TitleText>
                    <S.IconWrapper onClick={handleClose}>
                        <Cross />
                    </S.IconWrapper>
                </S.ModalHeaderTitle>

                <S.Container>
                    <Text type="h4" medium center mb={12}>
                        {t('subscription.cancellation.feedbackForm.title')}
                    </Text>
                    <S.Form onSubmit={handleSubmit}>
                        <Box $mb={30}>
                            <S.Textarea
                                rows={5}
                                maxLength={255}
                                name="comment"
                                $hasError={false}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.comment}
                                placeholder={t('subscription.cancellation.feedbackForm.input.placeholder')}
                            />
                            <S.TextAreaTip $paddingX={12} $paddingTop={6}>
                                <Text bold type="caption" color={isError ? 'critical-default' : 'text-secondary'}>
                                    {t('subscription.cancellation.feedbackForm.input.hint')}
                                </Text>
                                <Text
                                    bold
                                    type="caption"
                                    color={(values.comment || '').length >= 255 ? 'critical-default' : 'text-secondary'}
                                >{`${(values.comment || '').length}/255`}</Text>
                            </S.TextAreaTip>
                        </Box>
                        <Button disabled={!isValid} onClick={handleSubmit}>
                            {t('basics.continue')}
                        </Button>
                    </S.Form>
                    <OutlinedButton dataLocator="freeMonthCancelSubscriptionButton" onClick={handleCancel}>
                        {t('subscription.text.cancel')}
                    </OutlinedButton>
                </S.Container>
            </S.ModalContent>
        </Modal>
    );
};

export default FeedbackFormModal;
